import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const Legal = () => {
    const location = useLocation();
    const initialTab = location.hash === '#privacy' ? 'privacy' : 'tos';
    const [activeTab, setActiveTab] = useState(initialTab);

    return (
        <div className="min-h-screen bg-[#070B14] text-white">
            {/* Ambient glow */}
            <div className="pointer-events-none fixed inset-0 overflow-hidden">
                <div className="absolute -top-40 left-1/2 h-[520px] w-[820px] -translate-x-1/2 rounded-full bg-cyan-500/10 blur-3xl" />
                <div className="absolute top-32 right-[-220px] h-[520px] w-[520px] rounded-full bg-blue-500/10 blur-3xl" />
            </div>

            <div className="relative z-10 container mx-auto px-4 max-w-3xl py-8">
                {/* Back button */}
                <Link
                    to="/"
                    className="inline-flex items-center text-white/55 hover:text-white transition-colors mb-8"
                >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back
                </Link>

                {/* Tab switcher */}
                <div className="flex gap-2 mb-8">
                    <button
                        onClick={() => setActiveTab('tos')}
                        className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                            activeTab === 'tos'
                                ? 'bg-gradient-to-r from-cyan-600 to-blue-600 text-white'
                                : 'bg-white/5 text-white/55 hover:text-white/75 ring-1 ring-white/10'
                        }`}
                    >
                        Terms of Service
                    </button>
                    <button
                        onClick={() => setActiveTab('privacy')}
                        className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                            activeTab === 'privacy'
                                ? 'bg-gradient-to-r from-cyan-600 to-blue-600 text-white'
                                : 'bg-white/5 text-white/55 hover:text-white/75 ring-1 ring-white/10'
                        }`}
                    >
                        Privacy Policy
                    </button>
                </div>

                {/* Content */}
                <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-6 sm:p-8 backdrop-blur">
                    {activeTab === 'tos' ? <TermsOfService /> : <PrivacyPolicy />}
                </div>

                <p className="text-xs text-white/30 text-center mt-8">
                    Last updated: February 10, 2026
                </p>
            </div>
        </div>
    );
};

const SectionHeading = ({ children }) => (
    <h2 className="text-lg font-semibold text-white mt-8 mb-3 first:mt-0">{children}</h2>
);

const Paragraph = ({ children }) => (
    <p className="text-sm leading-relaxed text-white/65 mb-4">{children}</p>
);

const TermsOfService = () => (
    <div>
        <h1 className="text-2xl font-bold text-white mb-2">Terms of Service</h1>
        <p className="text-xs text-white/40 mb-6">Effective: February 10, 2026</p>

        <SectionHeading>1. Acceptance of Terms</SectionHeading>
        <Paragraph>
            By accessing or using CertCrammer ("the Service"), you agree to be bound by these Terms of Service. If you do not agree to these terms, do not use the Service. We reserve the right to update these terms at any time, and continued use of the Service constitutes acceptance of any changes.
        </Paragraph>

        <SectionHeading>2. Description of Service</SectionHeading>
        <Paragraph>
            CertCrammer is an online study tool that provides practice questions, flashcards, and performance analytics for the CompTIA Security+ (SY0-701) certification exam. The Service is intended as a supplementary study aid and does not guarantee certification or any particular exam result.
        </Paragraph>

        <SectionHeading>3. Accounts and Registration</SectionHeading>
        <Paragraph>
            You must sign in with a valid Google account to access the Service. You are responsible for maintaining the security of your account and for all activities that occur under your account. You must be at least 13 years old to use the Service.
        </Paragraph>

        <SectionHeading>4. Paid Subscriptions</SectionHeading>
        <Paragraph>
            CertCrammer offers both monthly subscription and one-time lifetime access plans. Monthly subscriptions renew automatically until canceled. You may cancel your subscription at any time through the Manage Subscription option, which opens the Stripe billing portal. Upon cancellation, you retain access until the end of your current billing period. Lifetime access is granted upon a single payment and does not expire.
        </Paragraph>
        <Paragraph>
            All payments are processed securely through Stripe. CertCrammer does not store your payment card details. Refund requests may be submitted within 7 days of purchase by contacting us; refunds are granted at our discretion.
        </Paragraph>

        <SectionHeading>5. Acceptable Use</SectionHeading>
        <Paragraph>
            You agree not to: (a) redistribute, resell, or share your account or any content from the Service; (b) attempt to reverse-engineer, scrape, or extract data from the Service; (c) use automated tools or bots to access the Service; (d) interfere with or disrupt the Service or its infrastructure; or (e) use the Service for any unlawful purpose.
        </Paragraph>

        <SectionHeading>6. Intellectual Property</SectionHeading>
        <Paragraph>
            All content on CertCrammer, including questions, explanations, flashcards, and software, is the property of CertCrammer and is protected by intellectual property laws. "CompTIA" and "Security+" are registered trademarks of CompTIA, Inc. CertCrammer is not affiliated with, endorsed by, or sponsored by CompTIA.
        </Paragraph>

        <SectionHeading>7. Disclaimer of Warranties</SectionHeading>
        <Paragraph>
            The Service is provided "as is" and "as available" without warranties of any kind, whether express or implied. We do not warrant that the Service will be uninterrupted, error-free, or that any content is accurate or complete. Your use of the Service is at your sole risk.
        </Paragraph>

        <SectionHeading>8. Limitation of Liability</SectionHeading>
        <Paragraph>
            To the maximum extent permitted by law, CertCrammer and its operators shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or relating to your use of the Service. Our total liability shall not exceed the amount you have paid to us in the 12 months preceding the claim.
        </Paragraph>

        <SectionHeading>9. Termination</SectionHeading>
        <Paragraph>
            We may suspend or terminate your access to the Service at any time, with or without cause, and with or without notice. Upon termination, your right to use the Service ceases immediately.
        </Paragraph>

        <SectionHeading>10. Governing Law</SectionHeading>
        <Paragraph>
            These Terms shall be governed by and construed in accordance with the laws of the United States. Any disputes arising from these Terms shall be resolved in the courts of competent jurisdiction.
        </Paragraph>

        <SectionHeading>11. Contact</SectionHeading>
        <Paragraph>
            If you have questions about these Terms of Service, please contact us via our X (Twitter) page at{' '}
            <a href="https://x.com/certcrammer" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300">
                @certcrammer
            </a>.
        </Paragraph>
    </div>
);

const PrivacyPolicy = () => (
    <div>
        <h1 className="text-2xl font-bold text-white mb-2">Privacy Policy</h1>
        <p className="text-xs text-white/40 mb-6">Effective: February 10, 2026</p>

        <SectionHeading>1. Information We Collect</SectionHeading>
        <Paragraph>
            When you sign in with Google, we receive your name, email address, and profile photo from your Google account. We also collect usage data including your practice session results, flashcard responses, domain mastery scores, and study time. This data is stored in Firebase Firestore and is associated with your user account.
        </Paragraph>

        <SectionHeading>2. How We Use Your Information</SectionHeading>
        <Paragraph>
            We use your information to: (a) provide and personalize the Service, including adaptive question selection and progress tracking; (b) process payments through Stripe; (c) communicate with you about your account or the Service; and (d) improve the Service based on aggregate usage patterns.
        </Paragraph>

        <SectionHeading>3. Payment Information</SectionHeading>
        <Paragraph>
            All payment processing is handled by Stripe. We do not store, access, or process your credit card number or payment card details on our servers. Stripe's handling of your payment information is governed by{' '}
            <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300">
                Stripe's Privacy Policy
            </a>.
        </Paragraph>

        <SectionHeading>4. Data Storage and Security</SectionHeading>
        <Paragraph>
            Your data is stored in Google Firebase (Firestore) and is protected by Firebase security rules that restrict access to authenticated users. We use HTTPS encryption for all data in transit. While we take reasonable measures to protect your data, no method of electronic storage is 100% secure.
        </Paragraph>

        <SectionHeading>5. Third-Party Services</SectionHeading>
        <Paragraph>
            The Service uses the following third-party services that may collect information: Google Firebase (authentication and data storage), Stripe (payment processing), and Google Analytics (anonymous usage statistics). Each service is governed by its own privacy policy.
        </Paragraph>

        <SectionHeading>6. Data Sharing</SectionHeading>
        <Paragraph>
            We do not sell, trade, or rent your personal information to third parties. We may share information only: (a) with service providers who assist in operating the Service (Firebase, Stripe); (b) if required by law or legal process; or (c) to protect the rights, safety, or property of CertCrammer or its users.
        </Paragraph>

        <SectionHeading>7. Data Retention</SectionHeading>
        <Paragraph>
            We retain your account data for as long as your account is active. If you wish to delete your account and associated data, please contact us. Upon account deletion, we will remove your personal data within 30 days, except where retention is required by law.
        </Paragraph>

        <SectionHeading>8. Cookies</SectionHeading>
        <Paragraph>
            The Service uses essential cookies required for authentication and session management via Firebase. We do not use third-party advertising cookies.
        </Paragraph>

        <SectionHeading>9. Children's Privacy</SectionHeading>
        <Paragraph>
            The Service is not intended for children under 13. We do not knowingly collect personal information from children under 13. If we become aware that we have collected such information, we will take steps to delete it.
        </Paragraph>

        <SectionHeading>10. Your Rights</SectionHeading>
        <Paragraph>
            You have the right to: (a) access the personal data we hold about you; (b) request correction of inaccurate data; (c) request deletion of your data; and (d) withdraw consent for data processing. To exercise these rights, contact us via our X (Twitter) page.
        </Paragraph>

        <SectionHeading>11. Changes to This Policy</SectionHeading>
        <Paragraph>
            We may update this Privacy Policy from time to time. We will notify you of material changes by posting the updated policy on this page with a revised effective date. Your continued use of the Service after changes constitutes acceptance of the updated policy.
        </Paragraph>

        <SectionHeading>12. Contact</SectionHeading>
        <Paragraph>
            If you have questions about this Privacy Policy, please contact us via our X (Twitter) page at{' '}
            <a href="https://x.com/certcrammer" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300">
                @certcrammer
            </a>.
        </Paragraph>
    </div>
);

export default Legal;
